import { faFilePen, faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Autocomplete, Button, Dialog, Grid, InputAdornment, TextField, Toolbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { api } from '../../api';
import { SimpleAlertProps } from '../../components/SimpleAlert';
import { EnumPage, EnumPageMap } from '../../data/PageMap';
import { UserGroup } from '../../api/user-group';
import { useGlobal } from '../../units/global/GlobalProvider';

export const useEditPageConfigDialog = (props: { simpleAlert: SimpleAlertProps }) => {
    const { simpleAlert } = props;
    const classes = useStyles();
    const { userGroups, setUserGroups, reloadUserGroups, pageConfigs, setPageConfigs, reloadPageConfigs } = useGlobal();
    const [open, setOpen] = useState(false);
    const [pageConfigId, setPageConfigId] = useState('');
    const [enumPage, setEnumPage] = useState<EnumPage | null>(null);
    const [inputPath, setInputPath] = useState('');
    const [originUserGroups, setOriginUserGroups] = useState<UserGroup[]>([]);
    const [inputUserGroups, setInputUserGroups] = useState<UserGroup[]>([]);
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    const onCreatePageConfig = async () => {
        await api.pageConfig
            .create({
                componentName: enumPage?.toString() || '',
                path: inputPath,
                userGroups: inputUserGroups,
            })
            .then((ret) => {
                if (ret.success) {
                    reloadPageConfigs().then(() => reloadUserGroups());;
                    setOpen(false);
                    simpleAlert?.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                } else {
                    simpleAlert?.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                }
            });
    };
    const onUpdatePageConfig = async () => {
        await api.pageConfig
            .update({
                pageConfigId: pageConfigId,
                componentName: enumPage?.toString() || '',
                path: inputPath,
                userGroups: inputUserGroups,
            })
            .then((ret) => {
                if (ret.success) {
                    reloadPageConfigs().then(() => reloadUserGroups());;
                    setOpen(false);
                    simpleAlert?.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                } else {
                    simpleAlert?.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                }
            });
    };
    const onReset = () => {
        setPageConfigId('');
        setEnumPage(null);
        setInputPath('');
        setOriginUserGroups([]);
        setInputUserGroups([]);
    };
    useEffect(() => {
        if (open) {
            reloadUserGroups();
        } else {
            onReset();
        }
    }, [open]);
    useEffect(() => {
        setInputUserGroups(
            userGroups.filter((userGroup) =>
                originUserGroups.map((originUserGroup) => originUserGroup.userGroupId).includes(userGroup.userGroupId!),
            ),
        );
    }, [userGroups]);
    useEffect(() => {
        setInputUserGroups(
            userGroups.filter((userGroup) =>
                originUserGroups.map((originUserGroup) => originUserGroup.userGroupId).includes(userGroup.userGroupId!),
            ),
        );
    }, [originUserGroups, userGroups]);
    return {
        classes,
        alert,
        open,
        setOpen,
        pageConfigId,
        setPageConfigId,
        enumPage,
        setEnumPage,
        inputPath,
        setInputPath,
        originUserGroups,
        setOriginUserGroups,
        inputUserGroups,
        setInputUserGroups,
        userGroups,
        setUserGroups,
        onOpen,
        onClose,
        onCreatePageConfig,
        onUpdatePageConfig,
        onReset,
    };
};

export interface EditPageConfigDialogProps extends ReturnType<typeof useEditPageConfigDialog> {}

export const EditPageConfigDialog = (props: EditPageConfigDialogProps): React.ReactElement => {
    const {
        classes,
        alert,
        open,
        setOpen,
        pageConfigId,
        setPageConfigId,
        enumPage,
        setEnumPage,
        inputPath,
        setInputPath,
        originUserGroups,
        setOriginUserGroups,
        inputUserGroups,
        setInputUserGroups,
        userGroups,
        setUserGroups,
        onOpen,
        onClose,
        onCreatePageConfig,
        onUpdatePageConfig,
        onReset,
    } = props;
    return (
        <Dialog fullWidth={true} maxWidth={'lg'} open={open} onClose={onClose}>
            <AppBar position='sticky' className={classes.appBar} color='primary'>
                <Toolbar>
                    <Typography style={{ width: '100%', textAlign: 'center' }} variant='h4' component={'div'}>
                        {pageConfigId.length > 0 ? '更新頁面' : '新增頁面'}
                    </Typography>
                    <Typography style={{ position: 'absolute', right: '0px' }} variant='h4' component={'div'}>
                        <Button color='inherit' onClick={onClose}>
                            <Typography variant='h5'>
                                <FontAwesomeIcon icon={faXmark} size='lg' fixedWidth />
                            </Typography>
                        </Button>
                    </Typography>
                </Toolbar>
            </AppBar>
            <Grid container flexDirection={'column'} padding={4}>
                <Grid container item flexDirection={'row'}>
                    <Grid container item flexDirection={'row'} xs={12}>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <Autocomplete
                                value={enumPage}
                                onChange={(event, newValue) => setEnumPage(newValue)}
                                options={Object.values(EnumPage)}
                                filterOptions={(options, state) => {
                                    const searchs = state.inputValue.split(/\s+/).filter((v) => v.length > 0);
                                    return options
                                        .filter((v, i, a) =>
                                            [
                                                searchs.length == 0
                                                    ? true
                                                    : searchs
                                                          .map(
                                                              (s) =>
                                                                  EnumPageMap[v].key.toUpperCase().includes(s.toUpperCase()) ||
                                                                  EnumPageMap[v].descr.toString().toUpperCase().includes(s.toUpperCase()),
                                                          )
                                                          .every((v) => v),
                                            ].every((v) => v),
                                        )
                                        .filter((v, i, a) => i < 500);
                                }}
                                renderOption={(props, option, state) => (
                                    <li
                                        {...props}
                                        key={JSON.stringify(option)}
                                    >{`${EnumPageMap[option].descr}【${EnumPageMap[option].key}】`}</li>
                                )}
                                getOptionLabel={(option) => EnumPageMap[option].descr}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label={
                                            <>
                                                <span style={{ color: 'red' }}>*</span>
                                                {'頁面元件'}
                                            </>
                                        }
                                    />
                                )}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                value={inputPath}
                                onChange={(e) => setInputPath(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <span style={{ color: 'red' }}>*</span>
                                        {'轉跳路徑'}
                                    </>
                                }
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12}>
                            <Autocomplete
                                multiple
                                value={inputUserGroups}
                                onChange={(event, newValue) => setInputUserGroups(newValue)}
                                options={userGroups}
                                getOptionLabel={(option) => option.descr!}
                                filterSelectedOptions
                                renderInput={(params) => <TextField {...params} label='可使用的使用者群組' placeholder='搜尋使用者群組' />}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <Button
                                disabled={![enumPage != null, inputPath.length > 0].every((v) => v)}
                                className={classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={pageConfigId.length > 0 ? onUpdatePageConfig : onCreatePageConfig}
                            >
                                <FontAwesomeIcon icon={pageConfigId.length > 0 ? faFilePen : faPlus} fixedWidth />
                                {pageConfigId.length > 0 ? '確定更新' : '確定新增'}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
};
